import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";

// EZ
import { EzFormComponent } from "../../../ez-form/ez-form.component";
import { InputConfig } from "../../../../types";
import { DocMeta } from "../../../../decorators";
import { EzPanelComponent } from "../../../ez-panel/panel/panel.component";
import { UiLibraryModule } from "../../../../template-ui.module";

type SeparatorModeId = "none" | "reviewers" | "actions" | "all";
type SeparatorTargets = "all" | readonly string[] | undefined;

interface SeparatorMode {
  id: SeparatorModeId;
  label: string;
  description: string;
  controls?: SeparatorTargets;
}

@DocMeta({
  hide: true,
  showcaseTitle: "EZ Form - Vertical Separators",
  showcaseDesc: `
  Demonstrates the optional \`verticalSeparator\` flag on each \`InputConfig\`.

  - Quickly flip between presets to see how separators interact with multi-row forms.
  - The form keeps the regular gap-only layout until a preset is selected.
  - Toggle specific controls or enable separators for every input.
  `
})
@Component({
    selector: "ui-form-vertical-separator-sample, ez-form-vertical-separator-sample",
    imports: [
        CommonModule,
        FormsModule,
        UiLibraryModule
    ],
    templateUrl: "./ez-form-vertical-separator-sample.component.html",
    styleUrls: ["./ez-form-vertical-separator-sample.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EzFormVerticalSeparatorSampleComponent {
  readonly separatorModes: readonly SeparatorMode[] = [
    {
      id: "none",
      label: "No separators",
      description: "Default spacing only - matches the component's baseline."
    },
    {
      id: "reviewers",
      label: "Reviewer row",
      description: "Adds separators before summary + reviewer controls.",
      controls: ["summary", "primaryReviewer", "secondaryReviewer"]
    },
    {
      id: "actions",
      label: "Status & actions",
      description: "Separators before status, priority, due date, and the update button.",
      controls: ["status", "priority", "dueDate", "requestOutcome"]
    },
    {
      id: "all",
      label: "Everything",
      description: "Applies separators before every rendered control.",
      controls: "all"
    }
  ];

  private readonly selectedMode = signal<SeparatorModeId>("none");
  private readonly currentMode = computed(() => this.separatorModes.find(mode => mode.id === this.selectedMode()));
  private readonly separatorTargets = computed<SeparatorTargets>(() => this.currentMode()?.controls);

  readonly activeModeDescription = computed<string>(() => this.currentMode()?.description ?? "");

  private readonly baseReviewInputs: InputConfig[] = [
    {
      type: "text",
      name: "requestTitle",
      label: "Request Title",
      width: "2x",
      row: 1,
      placeholder: "Q4 Readiness Review"
    },
    {
      type: "select",
      name: "status",
      label: "Status",
      width: "1x",
      row: 1,
      defaultValue: "open",
      options: [
        { value: "open", viewValue: "Open" },
        { value: "blocked", viewValue: "Blocked" },
        { value: "closed", viewValue: "Closed" }
      ]
    },
    {
      type: "select",
      name: "priority",
      label: "Priority",
      width: "1x",
      row: 1,
      defaultValue: "medium",
      options: [
        { value: "low", viewValue: "Low" },
        { value: "medium", viewValue: "Medium" },
        { value: "high", viewValue: "High" }
      ]
    },
    {
      type: "text",
      name: "requestOwner",
      label: "Owner",
      width: "1x",
      row: 1,
      placeholder: "Alex Rivera"
    },
    {
      type: "textarea",
      name: "summary",
      label: "Summary",
      width: "fill",
      row: 2,
      textAreaRows: 3,
      placeholder: "Capture the intent of the request..."
    },
    {
      type: "text",
      name: "primaryReviewer",
      label: "Primary Reviewer",
      width: "1x",
      row: 3,
      placeholder: "Pat Blake"
    },
    {
      type: "text",
      name: "secondaryReviewer",
      label: "Secondary Reviewer",
      width: "1x",
      row: 3,
      placeholder: "Jordan Lee"
    },
    {
      type: "date",
      name: "dueDate",
      label: "Due Date",
      width: "1x",
      row: 3
    },
    {
      type: "button",
      name: "requestOutcome",
      buttonText: "Submit Update",
      row: 3,
      width: "1x"
    }
  ];

  readonly reviewInputs = computed<InputConfig[]>(() => {
    const targets = this.separatorTargets();
    return this.baseReviewInputs.map(control => ({
      ...control,
      verticalSeparator: this.shouldApplySeparator(control.name, targets)
    }));
  });

  selectMode(id: SeparatorModeId): void {
    this.selectedMode.set(id);
  }

  modeIsSelected(id: SeparatorModeId): boolean {
    return this.selectedMode() === id;
  }

  trackMode(_: number, mode: SeparatorMode): SeparatorModeId {
    return mode.id;
  }

  private shouldApplySeparator(controlName: string, targets: SeparatorTargets): boolean {
    if (!targets) {
      return false;
    }

    if (targets === "all") {
      return true;
    }

    return targets.includes(controlName);
  }
}
